// SendFeedback.tsx
import React, { useState } from "react";
import * as AlfredoStyles from "../../../../styles/components/configuration/sections/accountSection/SendFeedback";

interface SendFeedbackProps {
  handleSave: (objectType: any) => void;
  updateState: (newState: any) => void;
}

const SendFeedback: React.FC<SendFeedbackProps> = ({
  handleSave,
  updateState,
}) => {
  const [feedback, setFeedback] = useState("");
  const [rating, setRating] = useState(0);
  const [category, setCategory] = useState("suggestion");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hoveredRating, setHoveredRating] = useState(0);

  const categories = [
    { value: "suggestion", label: "Sugestão", icon: "💡" },
    { value: "bug", label: "Reportar Bug", icon: "🐛" },
    { value: "praise", label: "Elogio", icon: "⭐" },
    { value: "question", label: "Dúvida", icon: "❓" },
  ];

  const handleSubmit = async () => {
    if (feedback.trim()) {
      setIsSubmitting(true);

      // Simula o envio do feedback
      try {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        handleSave("feedback");
        setFeedback("");
        setRating(0);
        setCategory("suggestion");
        updateState({
          message:
            "Feedback enviado com sucesso! Obrigado pela sua contribuição.",
          messageType: "success",
        });
      } catch (error) {
        updateState({
          message: "Erro ao enviar feedback. Tente novamente.",
          messageType: "error",
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const getRatingText = (rating: number) => {
    const ratings = {
      1: "Péssimo",
      2: "Ruim",
      3: "Regular",
      4: "Bom",
      5: "Excelente",
    };
    return ratings[rating as keyof typeof ratings] || "Avalie sua experiência";
  };

  return (
    <AlfredoStyles.FieldsContainer>
      {/* Rating com Melhor UX */}
      <div style={{ marginBottom: "var(--sp-32)" }}>
        {/* Stars Container */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "var(--sp-12)",
          }}
        >
          <div style={{ display: "flex", gap: "var(--sp-4)" }}>
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoveredRating(star)}
                onMouseLeave={() => setHoveredRating(0)}
                style={{
                  fontSize: "var(--fs-6xl)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color:
                    star <= (hoveredRating || rating) ? "#ffc107" : "#e1e5e9",
                  transition: "all 0.2s ease",
                  transform:
                    star <= (hoveredRating || rating)
                      ? "scale(1.1)"
                      : "scale(1)",
                  padding: "var(--sp-4)",
                }}
                aria-label={`Avaliar com ${star} estrela${star > 1 ? "s" : ""}`}
              >
                {star <= (hoveredRating || rating) ? "★" : "☆"}
              </button>
            ))}
          </div>

          {/* Rating Text */}
          <div
            style={{
              fontSize: "var(--fs-base)",
              fontWeight: 500,
              color: rating > 0 ? "#495057" : "#6c757d",
              textAlign: "center",
              minHeight: "calc(var(--u) * 24)",
            }}
          >
            {getRatingText(rating)}
          </div>
        </div>
      </div>

      {/* Categoria com Ícones */}
      <AlfredoStyles.InputWrapper>
        <label>Categoria do Feedback</label>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(calc(var(--u) * 120), 1fr))",
            gap: "var(--sp-12)",
            marginTop: "var(--sp-8)",
          }}
        >
          {categories.map((cat) => (
            <div
              key={cat.value}
              onClick={() => setCategory(cat.value)}
              style={{
                border: `2px solid ${
                  category === cat.value ? "#0066cc" : "#e1e5e9"
                }`,
                borderRadius: "12px",
                padding: "var(--sp-16) var(--sp-12)",
                textAlign: "center",
                cursor: "pointer",
                backgroundColor: category === cat.value ? "#f0f7ff" : "white",
                transition: "all 0.3s ease",
                transform: category === cat.value ? "scale(1.02)" : "scale(1)",
              }}
              onMouseEnter={(e) => {
                if (category !== cat.value) {
                  e.currentTarget.style.backgroundColor = "#f8f9fa";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }
              }}
              onMouseLeave={(e) => {
                if (category !== cat.value) {
                  e.currentTarget.style.backgroundColor = "white";
                  e.currentTarget.style.transform = "scale(1)";
                }
              }}
            >
              <div style={{ fontSize: "var(--fs-2xl)", marginBottom: "var(--sp-8)" }}>
                {cat.icon}
              </div>
              <div
                style={{
                  fontWeight: category === cat.value ? 600 : 500,
                  fontSize: "var(--fs-md)",
                }}
              >
                {cat.label}
              </div>
            </div>
          ))}
        </div>
      </AlfredoStyles.InputWrapper>

      {/* Feedback Text */}
      <AlfredoStyles.InputWrapper>
        <label>
          Seu Feedback
          <span
            style={{
              color: feedback.length > 500 ? "#dc3545" : "#6c757d",
              fontSize: "var(--fs-xs)",
              marginLeft: "var(--sp-8)",
            }}
          >
            {feedback.length}/500
          </span>
        </label>
        <textarea
          value={feedback}
          onChange={(e) => {
            if (e.target.value.length <= 500) {
              setFeedback(e.target.value);
            }
          }}
          placeholder="Descreva detalhadamente sua sugestão, problema ou elogio. Quanto mais específico, melhor podemos ajudar!"
          rows={6}
          style={{
            width: "100%",
            padding: "var(--sp-16)",
            border: `1px solid ${
              feedback.length > 500 ? "#dc3545" : "#e1e5e9"
            }`,
            borderRadius: "8px",
            fontSize: "var(--fs-md)",
            resize: "vertical",
            fontFamily: "inherit",
            boxSizing: "border-box",
            transition: "all 0.2s ease",
          }}
        />
        {feedback.length > 450 && (
          <div
            style={{
              color: feedback.length > 500 ? "#dc3545" : "#ffc107",
              fontSize: "var(--fs-xs)",
              marginTop: "var(--sp-4)",
            }}
          >
            {feedback.length > 500
              ? "Limite de caracteres excedido"
              : "Você está chegando perto do limite"}
          </div>
        )}
      </AlfredoStyles.InputWrapper>

      {/* Dicas Contextuais */}
      <div
        style={{
          backgroundColor: "#e7f3ff",
          border: "1px solid #b3d9ff",
          borderRadius: "8px",
          padding: "var(--sp-16)",
          marginBottom: "var(--sp-24)",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: "var(--sp-12)" }}>
          <div style={{ fontSize: "var(--fs-base)", flexShrink: 0 }}>💡</div>
          <div style={{ fontSize: "var(--fs-md)", color: "#0066cc" }}>
            <strong>Dica para um feedback eficaz:</strong>
            <ul style={{ margin: "var(--sp-8) 0 0 0", paddingLeft: "var(--sp-20)" }}>
              <li>Seja específico e inclua exemplos quando possível</li>
              <li>Para bugs, descreva os passos para reproduzir o problema</li>
              <li>Compartilhe o que você esperava que acontecesse</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Botão de Envio */}
      <AlfredoStyles.ButtonWrapper>
        <AlfredoStyles.Button
          onClick={handleSubmit}
          disabled={!feedback.trim() || feedback.length > 500 || isSubmitting}
          style={{
            opacity: feedback.trim() && feedback.length <= 500 ? 1 : 0.6,
            minWidth: "calc(var(--u) * 160)",
            position: "relative",
          }}
        >
          {isSubmitting ? (
            <>
              <div
                style={{
                  width: "calc(var(--u) * 16)",
                  height: "calc(var(--u) * 16)",
                  border: "2px solid transparent",
                  borderTop: "2px solid currentColor",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              />
              Enviando...
            </>
          ) : (
            <>
              <AlfredoStyles.MaterialIcons>send</AlfredoStyles.MaterialIcons>
              Enviar Feedback
            </>
          )}
        </AlfredoStyles.Button>
      </AlfredoStyles.ButtonWrapper>

      {/* Informações Adicionais */}
      <AlfredoStyles.SectionTitle
        style={{ fontSize: "var(--fs-xl)", marginTop: "calc(var(--u) * 48)", marginBottom: "var(--sp-20)" }}
      >
        Participe do Desenvolvimento
      </AlfredoStyles.SectionTitle>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(calc(var(--u) * 280), 1fr))",
          gap: "var(--sp-20)",
          marginTop: "var(--sp-16)",
        }}
      >
        <div
          style={{
            padding: "var(--sp-24)",
            border: "1px solid #e1e5e9",
            borderRadius: "12px",
            backgroundColor: "white",
            transition: "all 0.3s ease",
            cursor: "pointer",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-4px)";
            e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.12)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "none";
          }}
        >
          <div style={{ fontSize: "var(--fs-4xl)", marginBottom: "var(--sp-12)" }}>📋</div>
          <div
            style={{ fontWeight: 600, marginBottom: "var(--sp-8)", fontSize: "var(--fs-base)" }}
          >
            Roadmap Público
          </div>
          <div
            style={{
              color: "#6c757d",
              fontSize: "var(--fs-md)",
              marginBottom: "var(--sp-16)",
              lineHeight: "1.5",
            }}
          >
            Veja o que estamos planejando para o futuro e acompanhe nosso
            progresso
          </div>
          <AlfredoStyles.SecondaryButton style={{ width: "100%" }}>
            Explorar Roadmap
          </AlfredoStyles.SecondaryButton>
        </div>

        <div
          style={{
            padding: "var(--sp-24)",
            border: "1px solid #e1e5e9",
            borderRadius: "12px",
            backgroundColor: "white",
            transition: "all 0.3s ease",
            cursor: "pointer",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-4px)";
            e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.12)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "none";
          }}
        >
          <div style={{ fontSize: "var(--fs-4xl)", marginBottom: "var(--sp-12)" }}>🗳️</div>
          <div
            style={{ fontWeight: 600, marginBottom: "var(--sp-8)", fontSize: "var(--fs-base)" }}
          >
            Votar em Funcionalidades
          </div>
          <div
            style={{
              color: "#6c757d",
              fontSize: "var(--fs-md)",
              marginBottom: "var(--sp-16)",
              lineHeight: "1.5",
            }}
          >
            Vote nas próximas funcionalidades a serem desenvolvidas e influencie
            nosso produto
          </div>
          <AlfredoStyles.SecondaryButton style={{ width: "100%" }}>
            Participar da Votação
          </AlfredoStyles.SecondaryButton>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </AlfredoStyles.FieldsContainer>
  );
};

export default SendFeedback;
